import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../services/AuthApiService';
import { boardApi } from '../services/BoardApiService';
import type { AuthSession, AuthUser } from '../types/auth';

const AUTH_SESSION_KEY = 'retrojet-auth-session';

interface AuthContextValue {
  user: AuthUser | null;
  sessionToken: string | null;
  isAuthenticated: boolean;
  isSigningIn: boolean;
  signInWithGoogle: (credential: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [session, setSession] = useState<AuthSession | null>(() => loadStoredSession());
  const [isSigningIn, setIsSigningIn] = useState(false);

  useEffect(() => {
    boardApi.setAuthSession(session?.sessionToken ?? null);
  }, [session]);

  const signInWithGoogle = useCallback(async (credential: string): Promise<void> => {
    setIsSigningIn(true);
    try {
      const nextSession = await authApi.signInWithGoogle(credential);
      setSession(nextSession);
      localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(nextSession));
    } finally {
      setIsSigningIn(false);
    }
  }, []);

  const signOut = useCallback((): void => {
    setSession(null);
    localStorage.removeItem(AUTH_SESSION_KEY);
    boardApi.setAuthSession(null);
    window.google?.accounts.id.disableAutoSelect();
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user: session?.user ?? null,
    sessionToken: session?.sessionToken ?? null,
    isAuthenticated: !!session,
    isSigningIn,
    signInWithGoogle,
    signOut,
  }), [isSigningIn, session, signInWithGoogle, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return value;
}

function loadStoredSession(): AuthSession | null {
  const raw = localStorage.getItem(AUTH_SESSION_KEY);
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isAuthSession(parsed)) return null;
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(AUTH_SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(AUTH_SESSION_KEY);
    return null;
  }
}

function isAuthSession(value: unknown): value is AuthSession {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return isAuthUser(record.user)
    && typeof record.sessionToken === 'string'
    && typeof record.expiresAt === 'string';
}

function isAuthUser(value: unknown): value is AuthUser {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return typeof record.id === 'string'
    && typeof record.name === 'string'
    && (record.role === 'admin' || record.role === 'user')
    && (record.email === undefined || typeof record.email === 'string')
    && (record.pictureUrl === undefined || typeof record.pictureUrl === 'string');
}
